import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchAlarmData } from '../../services/dashboard/AlarmHistoryService';
import { AlarmHistoryItem } from '../well-alarms/AlarmHistoryModal';

interface AlarmState {
    data: AlarmHistoryItem[] | null;
    loading: boolean;
    error: string | null;
    hasMoreData: boolean;
}
const initialState: AlarmState = {
    data: null,
    loading: false,
    error: null,
    hasMoreData: true,
};

export const fetchAlarmDataAsync = createAsyncThunk<
    { alarmItems: AlarmHistoryItem[]; append: boolean },
    {
        assetId: string;
        startDate?: string;
        endDate?: string;
        filterBy?: string;
        filterValue?: string;
        orderBy?: string;
        orderColumn?: string;
        appendData?: boolean;
    }
>('alarm/fetchAlarmData', async (params) => {
    const {
        assetId,
        startDate,
        endDate,
        filterBy,
        filterValue,
        orderBy,
        orderColumn,
        appendData,
    } = params;

    const data = await fetchAlarmData(
        assetId,
        startDate,
        endDate,
        filterBy,
        filterValue,
        orderBy,
        orderColumn
    );

    return { alarmItems: data, append: appendData ?? false };
});

const AlarmHistorySlice = createSlice({
    name: 'AlarmHistorySlice',
    initialState,
    reducers: {
        resetAlarmState: (state) => {
            state.data = null;
            state.error = null;
            state.loading = false;
            state.hasMoreData = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAlarmDataAsync.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchAlarmDataAsync.fulfilled, (state, action) => {
                state.loading = false;
                const newItems = action.payload.alarmItems;

                if (action.payload.append && state.data) {
                    state.data = [...state.data, ...newItems];
                } else {
                    state.data = newItems;
                }

                // If we received fewer items than expected (e.g., less than page size), 
                // assume no more data is available
                state.hasMoreData = newItems.length > 0; // You might want to adjust this logic based on your API
            })
            .addCase(fetchAlarmDataAsync.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message ?? 'Failed to fetch alarms';
            });
    },
});

export const { resetAlarmState } = AlarmHistorySlice.actions;
export default AlarmHistorySlice.reducer;
